/**
 * Per-member, per-org, per-device flags for the org checklist — localStorage
 * for the same reasons as gettingStarted/checklistStorage.ts (no server
 * column, worst case of a second device is one more tap). Every storage touch
 * is guarded; the in-memory copy keeps the flags working in private mode.
 */
import type { OrgChecklistTier } from "./orgChecklist";

const FLAGS = [
  "visitedBoard",
  "visitedMembers",
  // Set when an invite to this org succeeds: org_invite_list never says who
  // made an invite, so this is the only way to know the step was done.
  "invited",
  // "Seen the all-set line" and "hid the card" are per tier, so a Simple
  // member promoted to Medio gets the card back with the new steps to do.
  // The Simple ones keep their original names (PR #836).
  "completedSeen",
  "completedSeenContributor",
  "hidden",
  "hiddenContributor",
] as const;
export type OrgChecklistFlag = (typeof FLAGS)[number];
export type OrgChecklistFlags = Record<OrgChecklistFlag, boolean>;

export const completedSeenFlag = (tier: OrgChecklistTier): OrgChecklistFlag =>
  tier === "simple" ? "completedSeen" : "completedSeenContributor";
export const hiddenFlag = (tier: OrgChecklistTier): OrgChecklistFlag =>
  tier === "simple" ? "hidden" : "hiddenContributor";

const PREFIX = "wexare-org-getting-started:";
export const NO_ORG_FLAGS: OrgChecklistFlags = Object.freeze(
  Object.fromEntries(FLAGS.map((f) => [f, false])) as OrgChecklistFlags,
);
const memory = new Map<string, OrgChecklistFlags>();
const listeners = new Set<() => void>();
// Bumped on every flag flip: the top-bar ring reads the flags of every org the
// member is in, and needs one stable snapshot for useSyncExternalStore.
let version = 0;

const keyOf = (userId: string, orgId: string) => `${userId}:${orgId}`;

export function readOrgFlags(userId: string, orgId: string): OrgChecklistFlags {
  const key = keyOf(userId, orgId);
  const cached = memory.get(key);
  if (cached) return cached;
  let flags = NO_ORG_FLAGS;
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Record<string, unknown>> | null;
      flags = Object.fromEntries(FLAGS.map((f) => [f, parsed?.[f] === true])) as OrgChecklistFlags;
    }
  } catch {
    // unreadable storage: start empty
  }
  memory.set(key, flags);
  return flags;
}

/** `value` false only un-hides: the other flags record things that happened. */
export function setOrgFlag(userId: string, orgId: string, flag: OrgChecklistFlag, value = true): void {
  const current = readOrgFlags(userId, orgId);
  if (current[flag] === value) return;
  const next = { ...current, [flag]: value };
  const key = keyOf(userId, orgId);
  memory.set(key, next);
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(next));
  } catch {
    // private mode / quota: the in-memory copy carries this session
  }
  version += 1;
  listeners.forEach((l) => l());
}

export function orgFlagsVersion(): number {
  return version;
}

// ─── Daily nudge ───────────────────────────────────────────────────────────
// The ring pulses once per calendar day (the device's), per member.
const NUDGE_PREFIX = "wexare-org-getting-started-nudge:";
const nudgeMemory = new Map<string, string>();

export function readNudgeDay(userId: string): string | null {
  if (nudgeMemory.has(userId)) return nudgeMemory.get(userId) ?? null;
  try {
    return localStorage.getItem(NUDGE_PREFIX + userId);
  } catch {
    return null;
  }
}

export function setNudgeDay(userId: string, day: string): void {
  nudgeMemory.set(userId, day);
  try {
    localStorage.setItem(NUDGE_PREFIX + userId, day);
  } catch {
    // private mode: the in-memory copy stops a second pulse this session
  }
}

export function subscribeOrgFlags(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function __resetOrgChecklistStorageForTests(): void {
  memory.clear();
  listeners.clear();
  nudgeMemory.clear();
  version = 0;
}
